import { useMemo } from "react";
import { useNavigate } from "react-router";
import { Loader2, Check, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { AssessmentsService, type AssessmentVariant } from "@/lib/api_client";
import { useAssessmentVariantMetadataList } from "@/hooks/api";
import { fetchAll } from "@/hooks/api/fetchAll";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const VARIANT_NAMES: Record<string, string> = {
  hearthealth: "Hjertehelse",
  stress: "Stress",
  fitness: "Fitness",
  bodyComposition: "Kroppssammensetning",
  sleep: "Søvn",
  other: "Annet",
  emphasizedBiometrics: "Viktige biomarkører",
  reccomendations: "Anbefalinger",
  summary: "Oppsummering",
};

function shortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${d.getDate().toString().padStart(2, "0")}.${(d.getMonth() + 1).toString().padStart(2, "0")}.${d.getFullYear().toString().slice(2)}`;
}

export function AssessmentVariantsPage() {
  const navigate = useNavigate();
  const { data: variants, isLoading: variantsLoading } = useQuery({
    queryKey: ["assessment-variants"],
    queryFn: () => fetchAll<AssessmentVariant>(AssessmentsService.getApiAssessmentVariants),
    staleTime: 10 * 60_000,
  });
  const { data: metadataList, isLoading: metadataLoading } = useAssessmentVariantMetadataList();

  const metadataByVariant = useMemo(() => {
    const map = new Map<string, (typeof metadataList extends (infer T)[] | undefined ? T : never)>();
    for (const m of metadataList ?? []) {
      map.set(m.assessmentVariantId, m);
    }
    return map;
  }, [metadataList]);

  const isLoading = variantsLoading || metadataLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-admin-text-subtle" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="border-b border-admin-card-border">
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">Variant</th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">Metadata</th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">Model</th>
                <th className="text-left text-xs font-medium text-admin-text-subtle p-3">Sist oppdatert</th>
              </tr>
            </thead>
            <tbody>
              {(variants ?? []).map((v) => {
                const meta = metadataByVariant.get(v.id);
                return (
                  <tr
                    key={v.id}
                    className="border-b border-admin-card-border last:border-0 hover:bg-admin-card/50 cursor-pointer"
                    onClick={() => navigate(`/assessment-variants/${v.id}`)}
                  >
                    <td className="p-3">
                      <span className="font-medium text-sm text-admin-text-primary">
                        {VARIANT_NAMES[v.variant] ?? v.variant}
                      </span>
                      <span className="text-xs text-admin-text-subtle ml-2">{v.variant}</span>
                    </td>
                    <td className="p-3">
                      {meta ? (
                        <Badge variant="secondary" className="gap-1">
                          <Check className="h-3 w-3" /> Definert
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1 text-admin-text-subtle">
                          <X className="h-3 w-3" /> Ikke definert
                        </Badge>
                      )}
                    </td>
                    <td className="p-3 text-sm text-admin-text-secondary">
                      {meta?.model ?? "-"}
                    </td>
                    <td className="p-3 text-sm text-admin-text-secondary">
                      {shortDate(meta?.updatedAt ?? v.updatedAt)}
                    </td>
                  </tr>
                );
              })}
              {(variants ?? []).length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-admin-text-subtle">
                    Ingen analysevarianter funnet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
